import styled from "@emotion/styled"
import { Backdrop, CircularProgress, Typography } from "@mui/material"
import * as api from "api"
import RatioElement from "common/ElementRatio"
import ErrorImage from "common/ErrorImage"
import { useIsDesktop } from "common/MediaQueries"
import ResponsiveModelImage from "common/ResponsiveModelImage"
import { memo, useEffect, useMemo, useRef, useState } from "react"
import Images360Manager, { Images360ManagerState } from "./Images360Manager"

const Styled = {
  Wrapper: styled.div<{ active: boolean; isFirst?: boolean }>`
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;

    ${(p) => p.theme.breakpoints.down("sm")} {
      margin: 10px 0;
    }

    ${(p) =>
      p.isFirst &&
      `
      position:absolute;
      top:0;
      left:0;
    `}

    cursor: ${(p) => (p.active ? "grab" : "inherit")};
    user-select: none;
    :focus {
      outline: none;
    }

    img {
      position: absolute;
      top: 0;
      inset-inline-end: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      opacity: 1;
    }

    img.hidden {
      opacity: 0;
    }
  `,
  Item: styled.div<{ vissible: boolean }>`
    display: ${(p) => (p.vissible ? "block" : "none")};
  `,
  Icon360: styled(Typography)`
    position: absolute;
    top: 15px;
    inset-inline-start: 20px;
    color: ${(p) => p.theme.colors.marine};
    z-index: 1001;

    :after {
      display: inline-block;
      content: "360";
      font-size: 16px;
      ${(p) => p.theme.breakpoints.up("md")} {
        font-size: 18px;
      }
    }

    :before {
      position: relative;
      display: inline-block;
      top: -12px;
      inset-inline-start: -4px;
      content: "";
      width: 4px;
      height: 4px;
      border-radius: 2px;
      border: solid 1px #707070;
      background-color: ${(p) => p.theme.colors.marine};
      ${(p) => p.theme.breakpoints.up("md")} {
        width: 6px;
        height: 6px;
      }
    }
  `,
  Backdrop: styled(Backdrop)`
    position: absolute;
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.75);
  `,
}

type Images360Props = { data: api.ImageModel[]; userChangeColor?: boolean }
const Images360 = ({ data, userChangeColor }: Images360Props) => {
  const ref = useRef<HTMLDivElement>()
  const isDesktop = useIsDesktop()
  const [state, setState] = useState<Images360ManagerState>(null)
  const manager = useMemo(() => new Images360Manager(ref, setState), [])

  useEffect(() => {
    manager.init()
    ref.current.focus()
    return () => manager.destroy()
  }, [manager])

  useEffect(() => {
    manager.load(data, isDesktop)
  }, [data, manager, isDesktop])

  const active = data?.length > 1

  const firstImage = data ? data[0] : null
  return (
    <RatioElement ratio={16 / 9}>
      <Styled.Backdrop open={state == "loading" || state == "error"}>
        {state == "loading" && (
          <>
            {!userChangeColor && (
              <Styled.Wrapper tabIndex={0} active={active} isFirst>
                {firstImage && (
                  <ResponsiveModelImage
                    image={firstImage}
                    imageSizes={["100vw", "642px"]}
                    priority
                  />
                )}
              </Styled.Wrapper>
            )}
            <CircularProgress color="primary" />
          </>
        )}
        {state == "error" && <ErrorImage />}
      </Styled.Backdrop>
      {active && <Styled.Icon360 />}
      <Styled.Wrapper tabIndex={0} ref={ref} active={active} />
    </RatioElement>
  )
}

export default memo(Images360)
